import { Intent } from '@blueprintjs/core';
import { ScifinderStatus } from '@retro-ui/api-contract';
import { captureException } from '@sentry/browser';
import { useContext, useEffect, useRef, useState } from 'react';

import { apiClient } from '@f/api-client';
import { AppToaster } from '@f/components/notification-toaster/AppToaster';
import { SciFinderAuthContext } from '@f/context';
import { sessionStorageClient } from '@f/services/storage/wrapper';
import { Exception } from '@f/types';

export interface RetrosynthesisLinkParams {
  searchId: string;
  resultId: string;
  sciFinderId?: string | null;
  onCreatedSearch?: () => void;
  onFailed?: () => void;
}

const BASE_RESULT_KEY = 'scifinder_base_result';

function getPollingTimeout(pollingCount: number) {
  if (pollingCount < 3) {
    return 2000;
  }
  if (pollingCount < 6) {
    return 5000;
  }
  return 10000;
}

const storage = sessionStorageClient;

function showToast(message: string, intent: Intent) {
  AppToaster.show(
    {
      message,
      intent,
    },
    'scifinderRetrosynthesisError'
  );
}

export const useSciFinderRetrosynthesis = (params: RetrosynthesisLinkParams) => {
  const { accessToken, authenticate } = useContext(SciFinderAuthContext);
  const [isGenerating, setGenerating] = useState(false);
  const [isCheckingStatus, setCheckingStatus] = useState(false);
  const [link, setLink] = useState<string>();
  const pollingCount = useRef(0);

  const openLink = (link: string) => {
    window.open(link, '_blank');
  };

  const checkStatus = async ({
    params,
    openLinkWhenComplete = false,
  }: {
    params: RetrosynthesisLinkParams;
    openLinkWhenComplete?: boolean;
  }) => {
    setGenerating(true);

    const response = await apiClient.retrosynthesis.fetchStatus({
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
      params,
      redirectUnauthorized: false,
    });

    if (!response) {
      return false;
    }
    const { status: requestStatus, body } = response;

    if (requestStatus !== 200) {
      return false;
    }

    const { status, link } = body;

    if ([ScifinderStatus.GENERATING_PLAN, ScifinderStatus.SAVED].includes(status)) {
      return false;
    }

    if (link) {
      setLink(link);

      if (openLinkWhenComplete) {
        openLink(link);
      }
    }

    setGenerating(false);

    if (
      [
        ScifinderStatus.EXPIRED,
        ScifinderStatus.FAILED,
        ScifinderStatus.NOT_FOUND,
        ScifinderStatus.UNAVAILABLE,
      ].includes(status)
    ) {
      if (openLinkWhenComplete) {
        showToast(`Failed to open retrosynthesis. SciFinder status is "${status}"`, Intent.DANGER);
      }

      if (params.onFailed) {
        params.onFailed();
      }
    }

    return true;
  };

  const pollStatus = async (options: { params: RetrosynthesisLinkParams; openLinkWhenComplete?: boolean }) => {
    const success = await checkStatus(options);

    if (success) {
      return;
    }

    const timeout = getPollingTimeout(pollingCount.current);
    pollingCount.current += 1;

    window.setTimeout(() => {
      pollStatus(options);
    }, timeout);
  };

  const openRetrosynthesis = async (params: RetrosynthesisLinkParams) => {
    if (isGenerating || isCheckingStatus) {
      return;
    }
    if (link) {
      openLink(link);
      return;
    }

    try {
      setGenerating(true);

      if (!accessToken) {
        storage.setItem(BASE_RESULT_KEY, JSON.stringify(params));
        await authenticate();
        return;
      }

      const response = await apiClient.retrosynthesis.createSynthesisLink({
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        params,
        redirectUnauthorized: false,
      });

      if (response.status === 401) {
        storage.setItem(BASE_RESULT_KEY, JSON.stringify(params));
        await authenticate();
        return;
      }

      const { status: requestStatus, body } = response;
      setGenerating(false);

      if (requestStatus !== 200) {
        const message = (body as Exception).message ?? '';
        showToast(`Failed to generate synthesis link. ${message}`, Intent.DANGER);
        return;
      }

      if (params.onCreatedSearch) {
        params.onCreatedSearch();
      }
      pollStatus({ params, openLinkWhenComplete: true });
    } catch (error: any) {
      setGenerating(false);

      const toastMessage = error instanceof Error ? error.message : (error as string);
      showToast(toastMessage, Intent.DANGER);

      captureException(error);
    }
  };

  useEffect(() => {
    if (!accessToken || isGenerating) {
      return;
    }

    const baseResult = storage.getItem(BASE_RESULT_KEY);

    if (!baseResult) {
      return;
    }

    storage.removeItem(BASE_RESULT_KEY);
    void openRetrosynthesis(JSON.parse(baseResult));
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken || isGenerating) {
      return;
    }

    async function initiallyCheckStatus() {
      try {
        setCheckingStatus(true);
        const response = await apiClient.retrosynthesis.fetchStatus({
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
          params,
          redirectUnauthorized: false,
        });
        const { status: requestStatus, body } = response;
        setCheckingStatus(false);

        if (requestStatus !== 200) {
          return;
        }

        const { status, link } = body;

        if (link) {
          setLink(link);
        }

        if ([ScifinderStatus.GENERATING_PLAN, ScifinderStatus.SAVED].includes(status)) {
          pollStatus({ params, openLinkWhenComplete: false });
        }
      } catch (error: any) {
        setCheckingStatus(false);
      }
    }

    if (!isCheckingStatus && params.sciFinderId) {
      initiallyCheckStatus();
    }
  }, [accessToken]);

  return {
    isLoading: isGenerating,
    isCheckingStatus,
    link,
    openLink,
    openRetrosynthesis,
  } as const;
};
